/**
 * Where a person comes from, as the provider's own mark.
 *
 * The directory this sheet lists is mostly one provider's, so every row
 * carried the same outlined word — a column of "Procore" down the right edge
 * that says nothing a reader can use, and that is the widest thing on the row
 * after the name. The mark is the same badge the sync chip, the queue row and
 * the record card already draw for that provider, so a reader who has learnt
 * it once on a card reads it here without re-reading.
 *
 * `files` keeps the word: a directory uploaded as a file has no brand mark to
 * draw, and inventing one would be the only glyph here that stands for
 * nothing.
 */
import { ProviderMark } from "../../record/ProviderMark";
import type { AssigneeSource } from "./assigneeIdentity";

const PROVIDER_NAME = { procore: "Procore", autodesk: "Autodesk" } as const;

/** LedAI is the default identity; only external directories need a badge. */
export function AssigneeSourceBadge({ source, size = 18 }: { source?: AssigneeSource; size?: number }) {
  if (!source || source === "ledai") return null;
  if (source === "files") {
    return <span data-assignee-source="files" style={{
      display: "inline-flex", alignItems: "center", flexShrink: 0,
      border: "1px solid currentColor", borderRadius: 4, padding: "1px 5px",
      fontSize: 9, lineHeight: "14px", fontWeight: 600, whiteSpace: "nowrap",
      color: "var(--muted-foreground, #716b61)",
    }}>File</span>;
  }
  // The mark itself is aria-hidden everywhere it is drawn; the name it stands
  // for lives on this wrapper so the row still reads as "… Procore".
  return (
    <span
      role="img"
      aria-label={PROVIDER_NAME[source]}
      title={PROVIDER_NAME[source]}
      data-testid="assignee-source-mark"
      data-assignee-source={source}
      style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}
    >
      <ProviderMark provider={source} size={size} />
    </span>
  );
}
